import React, { useEffect, useMemo, useState } from "react";
import { Sparkles, ListOrdered, Scale, Bell, Loader2, Plus, X, GripVertical, ArrowUp, ArrowDown, Clock, CheckCircle2, Flame } from "lucide-react";
import { toast } from "sonner";
import customerApi from "./customerApi";

const AUBERGINE = "var(--hq-deep, #5A4565)";
const PLUM = "var(--hq-accent, #6F4A7A)";
const BERRY = "#6E1E3A";
const EDGE = "#EFE4CC";
const GREEN = "#2F4A2A";
const GOLD = "#C49247";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const POP = {
  low: { label: "Low demand", bg: "#EAF0E6", fg: GREEN },
  medium: { label: "Medium demand", bg: "#F7EBD4", fg: "#7A5A1E" },
  high: { label: "High demand", bg: "#F5DEE4", fg: BERRY },
};

export default function HQFairSale() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [picks, setPicks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await customerApi.get("/customer/fairsale");
      setData(r.data);
      setPicks(r.data?.wishlist?.picks || []);
      setDirty(false);
    } catch { toast.error("Couldn't load the sale."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const release = data?.release;
  const items = data?.items || [];
  const byId = useMemo(() => Object.fromEntries(items.map((i) => [i.variety_id, i])), [items]);

  const save = async (next = picks) => {
    setSaving(true);
    try {
      await customerApi.put("/customer/fairsale/wishlist", { picks: next });
      setDirty(false);
      toast.success("Dream list saved.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save."); }
    finally { setSaving(false); }
  };

  const toggle = (vid) => {
    if (picks.includes(vid)) { setPicks(picks.filter((p) => p !== vid)); setDirty(true); return; }
    if (picks.length >= (release?.max_wishes || 15)) { toast.error(`Maximum ${release?.max_wishes || 15} varieties on your list.`); return; }
    setPicks([...picks, vid]); setDirty(true);
  };

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= picks.length) return;
    const next = [...picks];
    [next[i], next[j]] = [next[j], next[i]];
    setPicks(next); setDirty(true);
  };

  const onDrop = (i) => {
    if (dragIdx === null || dragIdx === i) { setDragIdx(null); return; }
    const next = [...picks];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(i, 0, moved);
    setPicks(next); setDirty(true); setDragIdx(null);
  };

  if (loading) return <div className="p-10" style={{ color: AUBERGINE }}><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!release) return <ComingSoon />;

  const wl = data.wishlist || {};
  const allocated = release.status === "allocated";

  return (
    <div data-testid="hq-fairsale" className="space-y-6" style={{ color: AUBERGINE }}>
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.45em] uppercase font-semibold" style={{ color: PLUM }}>✦ the Fairitage Tuber Sale</div>
          <h1 className="text-4xl md:text-5xl mt-1.5 leading-tight">{release.name}</h1>
        </div>
        {release.is_open && <Countdown closesAt={release.closes_at} />}
      </header>

      {allocated ? (
        <AllocationResult wl={wl} order={data.order} payHours={release.payment_window_hours} />
      ) : !release.is_open ? (
        <div className="bg-white border rounded-md p-6 max-w-2xl" style={{ borderColor: EDGE }} data-testid="fairsale-locked">
          <Scale className="w-6 h-6 mb-2" style={{ color: BERRY }} />
          <div className="font-semibold text-lg">The sale window has closed.</div>
          <p className="text-sm opacity-75 mt-1">
            Your dream list is locked in{picks.length ? ` (${picks.length} varieties)` : ""}. Allocation is underway ,
            you'll see your results right here the moment it's done.
          </p>
        </div>
      ) : (
        <>
          <DreamList picks={picks} byId={byId} release={release} dirty={dirty} saving={saving}
            onSave={() => save()} onRemove={toggle} onMove={move}
            dragIdx={dragIdx} setDragIdx={setDragIdx} onDrop={onDrop} />
          <Browse items={items} picks={picks} onToggle={toggle} />
        </>
      )}
    </div>
  );
}

function Countdown({ closesAt }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(t); }, []);
  if (!closesAt) return null;
  const diff = new Date(closesAt).getTime() - now;
  if (Number.isNaN(diff) || diff <= 0) return null;
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return (
    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 bg-white" style={{ borderColor: `${BERRY}44`, color: BERRY }} data-testid="fairsale-countdown">
      <Clock className="w-4 h-4" />
      <span className="text-sm font-semibold">Closes in {d > 0 ? `${d}d ` : ""}{h}h {d === 0 ? `${m}m` : ""}</span>
    </div>
  );
}

function DreamList({ picks, byId, release, dirty, saving, onSave, onRemove, onMove, dragIdx, setDragIdx, onDrop }) {
  return (
    <div className="bg-white border-2 rounded-md p-5" style={{ borderColor: `${BERRY}33` }} data-testid="dream-list">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <div className="font-semibold text-lg inline-flex items-center gap-2"><ListOrdered className="w-5 h-5" style={{ color: BERRY }} /> My Dream List</div>
          <div className="text-xs opacity-70 mt-0.5">
            {picks.length}/{release.max_wishes} varieties · drag (or use arrows) to rank · you'll receive up to {release.max_tubers} tubers
          </div>
        </div>
        <button type="button" onClick={onSave} disabled={saving || !dirty} data-testid="wishlist-save"
          className="h-10 px-5 rounded-full text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-40" style={{ backgroundColor: BERRY }}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          {dirty ? "Save my list" : "Saved"}
        </button>
      </div>
      {picks.length === 0 ? (
        <div className="text-sm italic opacity-60 py-4" data-testid="dream-list-empty">Nothing yet, add varieties from the release below. Number 1 is your most-wanted.</div>
      ) : (
        <ol className="space-y-1.5">
          {picks.map((vid, i) => {
            const it = byId[vid];
            if (!it) return null;
            return (
              <li key={vid} draggable
                onDragStart={() => setDragIdx(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(i)}
                className="flex items-center gap-2.5 border rounded px-3 py-2 bg-white cursor-grab"
                style={{ borderColor: dragIdx === i ? BERRY : EDGE }}
                data-testid={`dream-item-${i}`}>
                <GripVertical className="w-4 h-4 opacity-30 shrink-0" />
                <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0" style={{ backgroundColor: i === 0 ? GOLD : PLUM }}>{i + 1}</span>
                {it.image && <img src={it.image} alt="" className="w-9 h-9 rounded object-cover shrink-0" />}
                <span className="flex-1 min-w-0 truncate font-semibold text-sm">{it.name} <span className="opacity-50 font-normal">· {it.tier} · {money(it.price)}</span></span>
                <button type="button" onClick={() => onMove(i, -1)} className="p-1 opacity-50 hover:opacity-100" aria-label="Move up" data-testid={`dream-up-${i}`}><ArrowUp className="w-4 h-4" /></button>
                <button type="button" onClick={() => onMove(i, 1)} className="p-1 opacity-50 hover:opacity-100" aria-label="Move down" data-testid={`dream-down-${i}`}><ArrowDown className="w-4 h-4" /></button>
                <button type="button" onClick={() => onRemove(vid)} className="p-1 text-red-700" aria-label="Remove" data-testid={`dream-remove-${i}`}><X className="w-4 h-4" /></button>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function Browse({ items, picks, onToggle }) {
  const tiers = [
    { key: "A", title: "A Release · Collectors' varieties", sub: "Rare and premium , fixed pricing, no bidding." },
    { key: "B", title: "B Release · Garden favourites", sub: "Beautiful and more available , friendlier pricing." },
  ];
  return (
    <div className="space-y-8">
      {tiers.map((t) => {
        const list = items.filter((i) => i.tier === t.key);
        if (!list.length) return null;
        return (
          <section key={t.key} data-testid={`tier-${t.key}`}>
            <div className="mb-3">
              <div className="font-semibold text-lg">{t.title}</div>
              <div className="text-xs opacity-60 italic">{t.sub}</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {list.map((it) => {
                const idx = picks.indexOf(it.variety_id);
                const pop = POP[it.popularity] || POP.low;
                return (
                  <div key={it.variety_id} className="bg-white border rounded-md overflow-hidden flex flex-col" style={{ borderColor: idx >= 0 ? BERRY : EDGE }} data-testid={`variety-card-${it.variety_id}`}>
                    {it.image ? <img src={it.image} alt={it.name} className="w-full aspect-[4/3] object-cover" /> : <div className="w-full aspect-[4/3]" style={{ backgroundColor: "#F3ECDD" }} />}
                    <div className="p-3 flex-1 flex flex-col">
                      <div className="flex items-start justify-between gap-2">
                        <span className="font-semibold text-sm leading-snug">{it.name}</span>
                        <span className="text-sm font-bold whitespace-nowrap" style={{ color: BERRY }}>{money(it.price)}</span>
                      </div>
                      <div className="text-[11px] opacity-60 mt-0.5">{[it.colour, it.height].filter(Boolean).join(" · ")}</div>
                      <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ backgroundColor: pop.bg, color: pop.fg }} data-testid={`pop-${it.variety_id}`}>
                          <Flame className="w-3 h-3" /> {pop.label}
                        </span>
                        {it.low_stock && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ backgroundColor: "#F5DEE4", color: BERRY }}>Low stock</span>}
                      </div>
                      <button type="button" onClick={() => onToggle(it.variety_id)} data-testid={`variety-toggle-${it.variety_id}`}
                        className="mt-3 h-9 rounded-full text-xs font-semibold inline-flex items-center justify-center gap-1.5 border-2 transition-colors"
                        style={idx >= 0 ? { backgroundColor: BERRY, borderColor: BERRY, color: "#fff" } : { borderColor: `${BERRY}55`, color: BERRY }}>
                        {idx >= 0 ? <>#{idx + 1} on my list · remove</> : <><Plus className="w-3.5 h-3.5" /> Add to dream list</>}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function AllocationResult({ wl, order, payHours }) {
  if (wl.status === "no_allocation") {
    return (
      <div className="bg-white border rounded-md p-6 max-w-2xl" style={{ borderColor: EDGE }} data-testid="fairsale-no-allocation">
        <div className="font-semibold text-lg">This time the garden ran out before we reached your picks. 💜</div>
        <p className="text-sm opacity-75 mt-1">Demand was huge and stock was limited. You'll have priority insight for the next release, keep your eye on the countdown.</p>
      </div>
    );
  }
  const items = wl.allocation || [];
  const paid = order?.payment_status === "paid";
  const shipped = order?.stage === "completed" || order?.stage === "shipping";
  const steps = [
    { label: "Allocated", done: true },
    { label: "Awaiting Payment", done: true, active: !paid },
    { label: "Paid", done: paid },
    { label: "Shipped", done: shipped },
  ];
  return (
    <div className="bg-white border-2 rounded-md p-6 max-w-3xl" style={{ borderColor: `${GREEN}44` }} data-testid="fairsale-allocation">
      <div className="font-semibold text-xl">Congratulations! 🌸 Your allocation:</div>
      <ul className="mt-3 space-y-1.5">
        {items.map((a) => (
          <li key={a.variety_id} className="flex items-center gap-2 text-sm" data-testid={`alloc-item-${a.variety_id}`}>
            <CheckCircle2 className="w-4 h-4" style={{ color: GREEN }} />
            <span className="font-semibold">{a.name}</span>
            <span className="opacity-60">· {a.tier} tier · {money(a.price)}</span>
          </li>
        ))}
      </ul>
      <div className="mt-3 font-semibold" data-testid="alloc-total">
        Total: {items.length} tuber{items.length === 1 ? "" : "s"} · {money(items.reduce((s, a) => s + a.price, 0))}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-1.5" data-testid="alloc-status-steps">
        {steps.map((s, i) => (
          <React.Fragment key={s.label}>
            {i > 0 && <span className="opacity-30">→</span>}
            <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold"
              style={s.done ? { backgroundColor: s.active ? GOLD : GREEN, color: "#fff" } : { backgroundColor: "#F0EAE0", color: AUBERGINE }}>
              {s.label}
            </span>
          </React.Fragment>
        ))}
      </div>
      {!paid && (
        <p className="text-sm mt-4 opacity-80">
          Your invoice with bank transfer details is in <a href="/account/orders" className="underline font-semibold" style={{ color: BERRY }}>My Orders</a>.
          Please pay within {payHours || 48} hours{wl.payment_due_at ? ` (by ${new Date(wl.payment_due_at).toLocaleString()})` : ""} to lock in your tubers.
        </p>
      )}
    </div>
  );
}

function ComingSoon() {
  return (
    <div data-testid="hq-fairsale" className="space-y-7" style={{ color: AUBERGINE }}>
      <header>
        <div className="text-[10px] tracking-[0.45em] uppercase font-semibold" style={{ color: PLUM }}>✦ the Fairitage Tuber Sale</div>
        <h1 className="text-5xl mt-1.5 leading-tight">Coming <em style={{ color: BERRY }}>soon.</em></h1>
        <p className="italic mt-2 text-base opacity-75 max-w-2xl">
          You're in the right place, when the sale opens, this is where you'll build and rank your dream list.
        </p>
      </header>
      <div className="bg-white rounded-md border p-6 md:p-8 max-w-2xl" style={{ borderColor: EDGE }}>
        <div className="space-y-5">
          <Row Icon={ListOrdered} title="Build your dream list" body="Save up to 15 varieties and drag them into your order of preference. Your number one is your most-wanted." />
          <Row Icon={Scale} title="Fair allocation, no checkout wars" body="When the sale closes, everyone receives their highest available choices round by round, up to 10 tubers each." />
          <Row Icon={Sparkles} title="A & B releases" body="Premium collectors' varieties and beautiful garden favourites, each with clear fixed pricing." />
          <Row Icon={Bell} title="You'll know the moment it opens" body="Keep an eye here and on the home page, the countdown will appear as soon as the release date is set." />
        </div>
      </div>
    </div>
  );
}

function Row({ Icon, title, body }) {
  return (
    <div className="flex items-start gap-3.5">
      <span className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: BERRY }}>
        <Icon className="w-4 h-4" />
      </span>
      <div>
        <div className="font-semibold">{title}</div>
        <div className="text-sm opacity-75 mt-0.5">{body}</div>
      </div>
    </div>
  );
}
